<template>
  <section class="fg-page">
    <!-- Header pleine largeur — même grammaire que l'étape Traversée. -->
    <header class="fg-page__header">
      <BackButton :disabled="submitting" @click="back" />
      <div class="fg-page__titles">
        <h1 class="fg-page__title">{{ t('booking.title') }}</h1>
        <p class="fg-page__subtitle">{{ t('booking.subtitle') }}</p>
      </div>
      <Stepper current="details" />
    </header>

    <div class="fg-page-with-summary">
    <div class="fg-page-with-summary__main fg-booking">
      <p class="fg-booking__hint">{{ t('booking.requiredHint') }}</p>

      <template v-for="g in passengerGroups" :key="g.group">
        <h2 class="fg-booking__section">
          {{ passengerGroups.length > 1
            ? t('booking.passengersGroup', { group: g.group, count: g.passengers.length })
            : t('booking.passengers') }}
        </h2>
        <PaxForm
          v-for="pax in g.passengers" :key="pax.order"
          :passenger="pax"
          :age-band="ageBandFor(pax.type)"
          :birth-date-bounds="birthDateBoundsFor(pax.type)"
          :age-error="ageErrorFor(pax)"
          @update="(patch) => updatePassenger(pax.order, patch)"
        />
      </template>

      <template v-if="hasVehicle">
        <h2 class="fg-booking__section">{{ t('booking.vehicle') }}</h2>
        <!-- Rappel du véhicule choisi à la recherche : marque/modèle,
             dimensions, galerie de toit si prise. -->
        <p v-if="vehicleRecap" class="fg-vehicle-recap">
          <img v-if="vehicleLogo" :src="vehicleLogo" :alt="vehicleRecap" class="fg-vehicle-recap__logo" />
          <Icon v-else name="car" :size="14" />
          <span>{{ vehicleRecap }}</span>
        </p>
        <div class="fg-form">
          <label class="fg-field">
            <span class="fg-field__label">
              {{ t('vehicle.plate') }}<span class="fg-field__req" aria-hidden="true">*</span>
            </span>
            <input
              v-model="vehiclePlate"
              type="text"
              required
              autocomplete="off"
              :class="{ 'is-invalid': vehicleErrors.plate }"
              @blur="vehicleTouched.plate = true"
            />
            <span v-if="vehicleErrors.plate" class="fg-field__error">{{ t('booking.required') }}</span>
          </label>
        </div>
      </template>

      <h2 class="fg-booking__section">{{ t('booking.contact') }}</h2>
      <div class="fg-form">
        <label class="fg-field">
          <span class="fg-field__label">
            {{ t('booking.fullName') }}<span class="fg-field__req" aria-hidden="true">*</span>
          </span>
          <input
            v-model="store.search.contact.name"
            type="text"
            required
            :class="{ 'is-invalid': contactErrors.name }"
            @blur="contactTouched.name = true"
          />
          <span v-if="contactErrors.name" class="fg-field__error">{{ t('booking.required') }}</span>
        </label>
        <label class="fg-field">
          <span class="fg-field__label">
            {{ t('booking.email') }}<span class="fg-field__req" aria-hidden="true">*</span>
          </span>
          <input
            v-model="store.search.contact.email"
            type="email"
            required
            :class="{ 'is-invalid': contactErrors.email }"
            @blur="contactTouched.email = true"
          />
          <span v-if="contactErrors.email" class="fg-field__error">
            {{ store.search.contact.email ? t('booking.invalidEmail') : t('booking.required') }}
          </span>
        </label>
        <label class="fg-field">
          <span class="fg-field__label">
            {{ t('booking.phone') }}<span class="fg-field__req" aria-hidden="true">*</span>
          </span>
          <input
            v-model="store.search.contact.phone"
            type="tel"
            required
            :class="{ 'is-invalid': contactErrors.phone }"
            @blur="contactTouched.phone = true"
          />
          <span v-if="contactErrors.phone" class="fg-field__error">{{ t('booking.required') }}</span>
        </label>
      </div>

      <ErrorBanner v-if="store.error" :error="store.error" />

      <footer class="fg-actionbar">
        <div class="fg-actionbar__total">
          <span class="fg-actionbar__total-label">{{ t('results.total') }}</span>
          <span class="fg-actionbar__total-value">{{ totalFormatted }}</span>
          <span class="fg-actionbar__total-note">{{ t('summary.taxesIncluded') }}</span>
        </div>
        <button class="fg-btn fg-btn--primary" :disabled="!canSubmit" @click="submit">
          {{ submitting ? t('booking.submitting') : t('booking.submit') }}
        </button>
      </footer>
    </div>
    <BookingSummary class="fg-page-with-summary__aside" />
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import { useStore } from '../store'
import { useApi } from '../api'
import type {
  TicketAvailability,
  TicketCreatePayload,
  TicketPassenger,
  TicketPricingTariff,
} from '../api'
import { useI18n } from '../i18n'
import { useRouter } from '../router'
import { useTracker } from '../tracker'
import { routeKey } from '../utils/track-meta'
import { BookingState, BookingStep, Direction } from '../../../shared/types/enums'
import type { Availability, Booking, Meal, Passenger, Place, Tariff } from '../../../shared/types'
import {
  activeGroups,
  availabilitiesForGroup,
  availabilityForPlace,
  birthDateBounds,
  buildGroupTicketPayloads,
  collectAllRequestItems,
  companyKey,
  computeBookingTotal,
  computeTariffsTotal,
  groupKey,
  passengersForGroup,
  passengersForPlace,
  resolveAgeBands,
  sailingKey,
  toTicketCreatePassenger,
  toTicketPricingTariff,
  placeWithDispatchOrders,
  toTicketAvailabilityLeg,
  validatePassengerBirthDate,
} from '../../../shared/rules'
import type { AgeBand, GroupNumber, GroupTicketParts, PaxKind } from '../../../shared/rules'
import { formatPrice, normalizeBrandLogo } from '../utils/format'
import { mealDir, selectedGroupMeals, hasMealOptions, mealsTotal } from '../utils/meals'
import { saveBookingToBein } from '../utils/beintravel'
import BackButton from './atoms/BackButton.vue'
import PaxForm from './atoms/PaxForm.vue'
import ErrorBanner from './atoms/ErrorBanner.vue'
import BookingSummary from './atoms/BookingSummary.vue'
import Stepper from './atoms/Stepper.vue'
import Icon from './Icon.vue'

const store = useStore()
const api = useApi()
const router = useRouter()
const tracker = useTracker()
const { t, locale } = useI18n()

// Jalon funnel : l'user atteint le formulaire passagers (dernière marche avant
// la création du dossier). pax = nombre de voyageurs saisis.
onMounted(() => {
  tracker.emitOnce('passengers_started', {
    passengers: store.search.passengers.length,
    route: routeKey(store.search.journeys),
  })
})

const submitting = ref(false)

// Saisie passagers GROUPÉE par sous-groupe : c'est `place.in_board.passengers`
// (dispatch backend) qui guide QUI saisit où — pas la liste plate. Fallback
// découpage par taille si la place ne porte pas l'allocation. Les objets édités
// sont ceux du store (par `order`) → la validation/le payload restent inchangés.
const passengerGroups = computed(() => {
  const dossier = store.search.passengers
  return activeGroups(dossier.length).map((group) => {
    const place = store.selectedPlaces.outward[groupKey(group)]
      ?? store.selectedPlaces.return[groupKey(group)]
      ?? null
    const passengers = passengersForPlace(place, dossier) ?? passengersForGroup(dossier, group)
    return { group, passengers }
  })
})

// Tous les tarifs sélectionnés (aller/retour × groupe 1/2) — source unique du
// total et du tracking, alignée Results/Options.
function allSelectedTariffs(): Tariff[] {
  return [
    store.selectedTariffs.outward.group1, store.selectedTariffs.outward.group2,
    store.selectedTariffs.return.group1, store.selectedTariffs.return.group2,
  ].filter((x): x is Tariff => x !== null && x !== undefined)
}

// Total affiché dans l'action bar — tarifs (devise display) + repas sélectionnés.
const totalFormatted = computed(() => {
  const total = computeBookingTotal({ tariffs: allSelectedTariffs() })
  return formatPrice(total.amount + mealsTotal(store), total.currency, locale.value)
})

// Touched state pour les champs Contact — n'affiche l'erreur qu'après blur.
const contactTouched = reactive({
  name: false,
  email: false,
  phone: false,
})

// Véhicule : la plaque d'immatriculation est obligatoire dès qu'un véhicule est
// sélectionné. Le VehicleDrawer ne la capture que pour les Moto ; on la saisit
// donc ici pour tous les types (voiture, van…).
const hasVehicle = computed(() => nonEmpty(store.search.vehicle.code))

const vehicleTouched = reactive({ plate: false })

const vehiclePlate = computed({
  get: () => (typeof store.search.vehicle.identification === 'string' ? store.search.vehicle.identification : ''),
  set: (value: string) => { store.search.vehicle.identification = value },
})

const vehicleErrors = computed(() => ({
  plate: hasVehicle.value && vehicleTouched.plate && !nonEmpty(vehiclePlate.value),
}))

// Rappel inline du véhicule : "Renault Clio · 4,06 m × 1,44 m · Galerie de toit +30 cm"
// Logo de marque du véhicule (catalogue brand via le drawer). normalizeBrandLogo
// neutralise la transfo Cloudinary `g_face` qui casse l'image.
const vehicleLogo = computed<string>(() => normalizeBrandLogo(store.search.vehicle?.logo))

const vehicleRecap = computed<string>(() => {
  const v = store.search.vehicle
  const parts: string[] = []
  const name = [v.brand_name, v.model].filter(Boolean).join(' ')
  if (name) parts.push(name)
  const len = Number(v.length)
  const h = Number(v.height)
  if (len > 0 && h > 0) parts.push(`${fmtMeters(len)} × ${fmtMeters(h)}`)
  else if (len > 0) parts.push(fmtMeters(len))
  const roofCm = Number(v.roof?.height)
  if (roofCm > 0) parts.push(t('booking.vehicleRoofExtra', { cm: roofCm }))
  return parts.join(' · ')
})

function fmtMeters(n: number): string {
  return `${new Intl.NumberFormat(locale.value, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n)} m`
}

const contactErrors = computed(() => {
  const c = store.search.contact
  return {
    name: contactTouched.name && !nonEmpty(c.name),
    email: contactTouched.email && (!nonEmpty(c.email) || !isValidEmail(c.email)),
    phone: contactTouched.phone && !nonEmpty(c.phone),
  }
})

const canSubmit = computed(() => {
  if (submitting.value || !api.orgId) return false
  const c = store.search.contact
  if (!nonEmpty(c.name) || !nonEmpty(c.phone) || !isValidEmail(c.email)) return false
  // Plaque obligatoire si un véhicule est sélectionné.
  if (hasVehicle.value && !nonEmpty(vehiclePlate.value)) return false
  // Tous les passagers doivent avoir first_name + last_name + birth_date +
  // numéro de passeport (obligatoire, requis par les providers à l'international),
  // et une date de naissance cohérente avec leur type (âge évalué au départ).
  return store.search.passengers.every(
    (p) => nonEmpty(p.first_name) && nonEmpty(p.last_name) && nonEmpty(p.birth_date)
      && nonEmpty(p.doc_number) && ageErrorFor(p) === null,
  )
})

// ── Âges : bandes du transporteur réservé + validation des dates de naissance ──
//
// Les bandes sont résolues pour la compagnie du sailing RETENU (elle est connue
// à ce stade), pas par intersection de toutes les compagnies de la route.
// Les dates de référence sont les `depart_at` de chaque leg : un passager doit
// rester dans sa bande à l'aller ET au retour.

const ageCompany = computed<string | null>(() => {
  for (const av of [...sailingPool('outward'), ...sailingPool('return')]) {
    const key = companyKey(av)
    if (key) return key
  }
  return null
})

const ageReferenceDates = computed<string[]>(() => {
  const dates = [...sailingPool('outward'), ...sailingPool('return')]
    .map((av) => av.depart_at)
    .filter((d): d is string => typeof d === 'string' && d !== '')
  if (dates.length > 0) return dates
  // Repli : dates de recherche, si aucune availability n'est encore retenue.
  return collectAllRequestItems(store.search)
    .flatMap((item) => [item.out_at, item.ret_at])
    .filter((d): d is string => typeof d === 'string' && d !== '')
})

const ageBands = computed(() => resolveAgeBands(store.search.journeys, { company: ageCompany.value }))

function ageBandFor(type: string | null | undefined): AgeBand | null {
  const kind = (['Adult', 'Child', 'Baby'] as const).includes(type as PaxKind)
    ? (type as PaxKind)
    : 'Adult'
  return ageBands.value[kind]
}

function birthDateBoundsFor(type: string | null | undefined) {
  return birthDateBounds(ageBandFor(type), ageReferenceDates.value)
}

/** `'FUTURE' | 'OUT_OF_BAND' | null`. L'absence et le format invalide sont déjà
 *  couverts par `nonEmpty` / l'input date natif — pas de double message. */
function ageErrorFor(passenger: Passenger): 'FUTURE' | 'OUT_OF_BAND' | null {
  const result = validatePassengerBirthDate(passenger, ageBands.value, ageReferenceDates.value)
  if (result.ok) return null
  return result.code === 'FUTURE' || result.code === 'OUT_OF_BAND' ? result.code : null
}

function nonEmpty(v: unknown): boolean {
  return typeof v === 'string' && v.trim().length > 0
}

function updatePassenger(order: number, patch: Partial<Passenger>): void {
  const target = store.search.passengers.find((p) => p.order === order)
  if (target) Object.assign(target, patch)
}

async function submit(): Promise<void> {
  // Si on appelle submit avec des champs invalides (ex : raccourci clavier sur
  // un bouton enabled à tort), on force tous les champs en `touched` pour
  // exposer les erreurs visuellement.
  if (!canSubmit.value) {
    contactTouched.name = true
    contactTouched.email = true
    contactTouched.phone = true
    vehicleTouched.plate = true
    return
  }
  submitting.value = true
  store.error = null
  store.state = BookingState.Booking
  try {
    // Un dossier > 4 pax = 2 sous-groupes → 1 ticket par groupe (même booking_id
    // / reference), comme le funnel SaaS. ≤ 4 pax → 1 seul payload (group 1).
    const payloads = assembleTicketPayloads()
    const bookings: Booking[] = []
    for (const payload of payloads) {
      bookings.push(await api.createTicket(payload))
    }
    const primaryBooking = bookings[0]!
    store.booking = primaryBooking

    // Le widget crée le(s) booking(s) côté FerryGate, PUIS le dossier BeinTravel :
    // on le construit depuis la sélection (tarifs + ports déjà en store,
    // contrairement au retour de createTicket qui les omet) et on corrèle via
    // `ferrygate_booking_id`. L'hôte injecte agency/domain et appelle actionSave.
    // Bloquant : un échec bein → page erreur.
    if (store.beintravelUrl && primaryBooking?.id) {
      const createdAt = typeof (primaryBooking as { created_at?: string }).created_at === 'string'
        ? (primaryBooking as { created_at?: string }).created_at!
        : nowDateTime()
      const beinResult = await saveBookingToBein(
        store.beintravelUrl,
        buildBeinDossier(payloads, String(primaryBooking.id), createdAt),
      )
      // Id du dossier bein → sert à la redirection vers le checkout de l'hôte.
      if (beinResult.id) store.beinBookingId = String(beinResult.id)
    }

    // Jalon funnel CLÉ : l'option (réservation) est créée — conversion primaire
    // à remonter à Google Ads (valeur + devise pour le ROAS, noms de ports
    // lisibles pour le bandeau hôte). Valeur = total des 2 groupes.
    const bookingTotal = computeBookingTotal({ tariffs: allSelectedTariffs() })
    const primaryJourney = store.search.journeys.find((j) => j.direction === Direction.Outward)
      ?? store.search.journeys[0]
    tracker.emit('option_created', {
      bookingId: primaryBooking?.id,
      bookingReference: primaryBooking?.reference ?? payloads[0]!.booking_reference,
      value: bookingTotal.amount + mealsTotal(store),
      currency: (bookingTotal.currency ?? '').toUpperCase(),
      originName: primaryJourney?.origin?.port_name,
      destinationName: primaryJourney?.destination?.port_name,
      contactName: store.search.contact.name,
      route: routeKey(store.search.journeys),
    })

    store.state = BookingState.Completed
    router.goTo(BookingStep.Confirmation)
  } catch (err) {
    store.error = {
      code: 'BOOKING_FAILED',
      message: err instanceof Error ? err.message : 'Booking failed',
    }
  } finally {
    submitting.value = false
  }
}

/**
 * Availability + place RÉSERVÉES pour (direction, groupe). L'availability est
 * cherchée UNIQUEMENT parmi celles du sailing sélectionné ET du groupe : les
 * place.id sont des modèles catalogue partagés entre TOUS les départs/groupes
 * du jour ; chercher trop large renverrait l'availability d'un autre horaire →
 * `id` ≠ `id` scellé dans le `price_token` → refus backend (signed quote).
 */
function legFor(direction: 'outward' | 'return', group: GroupNumber): { av: Availability; place: Place | null } | null {
  const place = store.selectedPlaces[direction][groupKey(group)] ?? null
  const av = availabilityForPlace(availabilitiesForGroup(sailingPool(direction), group), place?.id)
    ?? (group === 1 ? store.availabilities[direction].primary ?? null : null)
  return av ? { av, place } : null
}

/** Leg FerryGate : `place.in_board.passengers` réduit au DISPATCH des `order`
 *  (cf. placeWithDispatchOrders). On PRÉSERVE l'allocation backend exacte
 *  (ex. group1 = 2,3,4,5 ; group2 = 1,6) ; fallback = sous-ensemble du groupe
 *  (découpage par taille). L'identité reste portée par le manifeste passengers[]. */
function toLeg(
  direction: 'Outward' | 'Return',
  group: GroupNumber,
  leg: { av: Availability; place: Place | null },
): TicketAvailability {
  const place = leg.place
    ? [placeWithDispatchOrders(leg.place, passengersForGroup(store.search.passengers, group))]
    : []
  // Repas choisis de CE sous-groupe/direction (quantité > 0), lus depuis la même
  // source que l'étape Options → persistés jusqu'au provider.
  const dir = mealDir(store, direction === 'Return' ? 'return' : 'outward')
  const meals = selectedGroupMeals(group === 1 ? dir.group1 : dir.group2)
  return toTicketAvailabilityLeg(direction, leg.av, place, meals)
}

/**
 * Payload(s) de création de ticket — UN par sous-groupe passagers (group 1, et
 * group 2 si > 4 pax). Le widget construit SES legs/tarifs par groupe (stratégie
 * de place propre) puis délègue l'enveloppe à l'assembleur PARTAGÉ
 * `buildGroupTicketPayloads` (`#ferry/shared`), commun au funnel SaaS — forme de
 * payload unique, zéro divergence.
 */
function assembleTicketPayloads(): TicketCreatePayload[] {
  if (!store.selectedTariffs.outward.group1) throw new Error('Outward tariff not selected')
  if (!api.orgId) throw new Error('Missing orgId')

  const isReturn = store.search.direction === Direction.Return
  // L'affectation passager → groupe est PORTÉE par `place.in_board.passengers`
  // (dispatch backend) ; on la préserve telle quelle dans `toLeg` — pas de
  // re-dérivation côté front.
  const groups: GroupTicketParts[] = activeGroups(store.search.passengers.length).map((group) => {
    const gk = groupKey(group)
    const availability: TicketAvailability[] = []
    const out = legFor('outward', group)
    if (out) availability.push(toLeg('Outward', group, out))
    if (isReturn) {
      const ret = legFor('return', group)
      if (ret) availability.push(toLeg('Return', group, ret))
    }
    const pricing = [
      toTicketPricingTariff(store.selectedTariffs.outward[gk], `out-${group}`),
      toTicketPricingTariff(store.selectedTariffs.return[gk], `ret-${group}`),
    ].filter((t): t is TicketPricingTariff => t !== null)
    // Prix des TARIFS (le prix des repas est ajouté côté BACKEND — cf. note ci-
    // dessous ; envoyé aussi dans `meals[]` pour que le backend puisse tarifer).
    const price = computeTariffsTotal(
      [store.selectedTariffs.outward[gk], store.selectedTariffs.return[gk]].filter((t): t is Tariff => Boolean(t)),
    )
    // Passagers de CE sous-groupe (dispatch `in_board`) → chaque dossier ne
    // porte QUE ses passagers (indépendance group1 / group2). Fallback découpage
    // par taille si la place ne porte pas l'allocation.
    const groupPlace = store.selectedPlaces.outward[gk] ?? store.selectedPlaces.return[gk] ?? null
    const groupPax = passengersForPlace(groupPlace, store.search.passengers)
      ?? passengersForGroup(store.search.passengers, group)
    // Repas choisis de CE sous-groupe (aller + retour) — agrégés au niveau ticket.
    const meals: Meal[] = [
      ...selectedGroupMeals(mealDir(store, 'outward')[gk]),
      ...(isReturn ? selectedGroupMeals(mealDir(store, 'return')[gk]) : []),
    ]
    return { availability, pricing, price, meals, passengers: groupPax.map(toTicketCreatePassenger) }
  })

  // CHAQUE sous-groupe = un dossier FerryGate INDÉPENDANT (booking_id +
  // reference distincts) → group2 jamais lié à group1. `newDossier()` renvoie un
  // nouveau dossier à chaque appel (1 par groupe).
  const payloads = buildGroupTicketPayloads(groups, {
    organization_id: api.orgId,
    currency: store.selectedTariffs.outward.group1.currency,
    contact: {
      name: store.search.contact.name,
      email: store.search.contact.email,
      phone: store.search.contact.phone,
      prefix: store.search.contact.prefix,
    },
    passengers: store.search.passengers.map(toTicketCreatePassenger),
    vehicle: store.search.vehicle.code ? store.search.vehicle : null,
    newDossier: () => ({ booking_id: generateUuid(), booking_reference: generateBookingRef() }),
  })
  if (payloads.length === 0) throw new Error('No availability selected')
  return payloads
}

// ════════════════════════════════════════════════════════════════════════
//  Dossier BeinTravel — mapping sélection-widget → payload `actionSave`.
//  L'hôte (bladia) injecte `agency_id`/`domain_id`. Reproduit la forme de
//  `mapFerrygateBookingToBeinPayload` (testée côté bladia) mais depuis la
//  sélection : les objets `Availability` du store portent les ports + la
//  compagnie + le gateway, absents du retour de createTicket.
// ════════════════════════════════════════════════════════════════════════

/** 1re lettre en majuscule (réplique le `ucfirst` PHP de getTariffsByType). */
function ucfirst(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s
}

/** Horodatage `Y-m-d H:i:s` (fallback si le booking FerryGate n'expose pas created_at). */
function nowDateTime(): string {
  const d = new Date()
  const p = (n: number): string => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`
}

/** Tarif → format backend (`type` capitalisé pour getTariffsByType). */
function normalizeBeinTariff(tf: TicketPricingTariff): Record<string, unknown> {
  return { ...tf, type: ucfirst(String(tf?.type || 'ticket')) }
}

/** Tarif d'un segment via `sourceKey` (`out-{group}` / `ret-{group}`) ; fallback = tous. */
function tariffsForSegment(tariffs: TicketPricingTariff[], direction: 'Outward' | 'Return', group: GroupNumber): Record<string, unknown>[] {
  const key = `${direction === 'Return' ? 'ret' : 'out'}-${group}`
  const matched = tariffs.filter((tf) => String(tf?.sourceKey || '').toLowerCase() === key)
  return (matched.length ? matched : tariffs).map(normalizeBeinTariff)
}

/** Code compagnie attendu par getJourney (`Company::find(['code' => name])`). */
function companyCode(company: { code?: string | number; name?: string } | null | undefined): string {
  return String(company?.code ?? company?.name ?? '')
}

/** Véhicule : array `[{…}]`, objet `{…}` ou absent → objet (jamais null). */
function normalizeBeinVehicle(rawVeh: unknown): Record<string, unknown> {
  if (Array.isArray(rawVeh)) return (rawVeh[0] as Record<string, unknown>) ?? {}
  return rawVeh && typeof rawVeh === 'object' ? (rawVeh as Record<string, unknown>) : {}
}

/** Passager → format backend bein. */
function mapBeinPassenger(p: TicketPassenger): Record<string, unknown> {
  return {
    order: p?.order ?? null,
    type: p?.type ?? null,
    param: p?.param ?? null,
    gender: p?.gender ?? null,
    age: p?.age ?? null,
    first_name: p?.first_name ?? '',
    last_name: p?.last_name ?? '',
    birth_date: p?.birth_date ?? null,
    nationality: p?.nationality ?? '',
    doc_type: '',
    doc_number: p?.doc_number ?? '',
    doc_expiration: '',
  }
}

/** Un segment réservé → une "jambe" aplatie attendue par getJourney + Ticket.
 *  `place.in_board.passengers` réduit au dispatch des `order` (cf.
 *  placeWithDispatchOrders) — dispatch backend exact préservé, identité via le
 *  manifeste passengers[]. */
function buildBeinLeg(
  av: Availability,
  direction: 'Outward' | 'Return',
  group: GroupNumber,
  selectedPlace: Place | null,
  pricingTariffs: TicketPricingTariff[],
  vehicle: unknown,
): Record<string, unknown> {
  const place = selectedPlace
    ? [placeWithDispatchOrders(selectedPlace, passengersForGroup(store.search.passengers, group))]
    : []
  return {
    id: av.id,
    group_id: av.group_id ?? group,
    direction,
    company: { name: companyCode(av.company) },
    gateway: av.gateway,
    journey_id: av.journey_id,
    depart_at: av.depart_at,
    depart_code: av.depart_code,
    depart_name: av.depart_name,
    arrive_at: av.arrive_at,
    arrive_code: av.arrive_code,
    arrive_name: av.arrive_name,
    ship_name: av.ship_name,
    places: place,
    tariffs: tariffsForSegment(pricingTariffs, direction, group),
    vehicle: normalizeBeinVehicle(vehicle),
    meals: [],
  }
}

/**
 * Payload `actionSave` depuis la sélection — UN dossier bein portant X TICKETS
 * (1 par dossier FerryGate = 1 par sous-groupe). Chaque ticket porte SON
 * `ferrygate_booking_id`/reference, SES passagers, SES tarifs et SES legs
 * (outward/return). Le dossier porte le contact, le manifeste COMPLET et le
 * `ferrygate_booking_id` PRIMAIRE (clé d'idempotence bein). `ferrygateBookingId`
 * = id du booking FerryGate primaire (corrélation hôte).
 *
 * NB : les champs agrégés dossier-level (`availability`/`pricing`) sont conservés
 * pour compat tant que bladia ne consomme pas encore `tickets[]`.
 */
function buildBeinDossier(payloads: TicketCreatePayload[], ferrygateBookingId: string, createdAt: string): Record<string, unknown> {
  const isReturn = store.search.direction === Direction.Return
  const primary = payloads[0]!
  const groups = activeGroups(store.search.passengers.length)

  // Manifeste COMPLET au niveau dossier (chaque payload ne porte QUE son
  // sous-groupe) — on reprend la sélection complète ; lead = order 1 (group1).
  const passengers = store.search.passengers.map(toTicketCreatePassenger).map(mapBeinPassenger)
  const lead = (passengers.find((p) => Number(p.order) === 1) ?? passengers[0] ?? {}) as Record<string, unknown>

  // UN ticket bein par sous-groupe (= par dossier FerryGate) ; les legs sont
  // aussi agrégés par direction (`outward`/`ret`) pour le dossier-level compat.
  const outward: Record<string, unknown>[] = []
  const ret: Record<string, unknown>[] = []
  const tickets = payloads.map((payload, i) => {
    const group = (groups[i] ?? (i + 1)) as GroupNumber
    const groupTariffs = payload.params.pricing.tariffs
    const tOut: Record<string, unknown>[] = []
    const tRet: Record<string, unknown>[] = []
    const out = legFor('outward', group)
    if (out) {
      const leg = buildBeinLeg(out.av, 'Outward', group, out.place, groupTariffs, payload.vehicle)
      tOut.push(leg); outward.push(leg)
    }
    if (isReturn) {
      const r = legFor('return', group)
      if (r) {
        const leg = buildBeinLeg(r.av, 'Return', group, r.place, groupTariffs, payload.vehicle)
        tRet.push(leg); ret.push(leg)
      }
    }
    return {
      ferrygate_booking_id: payload.booking_id,
      booking_reference: payload.booking_reference,
      currency: payload.currency,
      pricing: { tariffs: groupTariffs.map(normalizeBeinTariff) },
      passengers: payload.passengers.map(mapBeinPassenger),
      vehicle: normalizeBeinVehicle(payload.vehicle),
      availability: { outward: tOut, ...(tRet.length ? { return: tRet } : {}) },
    }
  })

  return {
    type: 'Ferry',
    // Requis par actionSave (Price.payment_date / invoice_date). Format
    // `Y-m-d H:i:s` (celui du booking FerryGate), sinon « Undefined array key ».
    created_at: createdAt,
    currency: primary.currency,
    contact: {
      name: primary.contact.name,
      first_name: lead.first_name ?? '',
      last_name: lead.last_name ?? '',
      email: primary.contact.email,
      phone: primary.contact.phone,
      prefix: primary.contact.prefix,
    },
    pricing: { tariffs: payloads.flatMap((p) => p.params.pricing.tariffs).map(normalizeBeinTariff) },
    meals: [],
    passengers,
    ferrygate_booking_id: ferrygateBookingId,
    booking_reference: primary.booking_reference,
    availability: { outward, ...(ret.length ? { return: ret } : {}) },
    // X TICKETS (1 par dossier FerryGate) — modèle cible bladia.
    tickets,
  }
}

/** Availabilities du sailing sélectionné pour la direction — même pool (et
 *  même ordre) que PlaceList/Results, pour que tarif et availability réservée
 *  désignent toujours la même offre. */
function sailingPool(direction: 'outward' | 'return'): Availability[] {
  const all = store.availabilities[direction].all ?? []
  const selected = store.selectedSailing[direction]
  if (!selected) return all
  return all.filter((av) => sailingKey(av) === selected)
}

function generateUuid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID()
  return `bk_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
}

function generateBookingRef(): string {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'
  let ref = ''
  for (let i = 0; i < 7; i++) ref += alphabet[Math.floor(Math.random() * alphabet.length)]
  return ref
}

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@.]+\.\S+$/.test(value)
}

function back(): void {
  router.goTo(hasMealOptions(store) ? BookingStep.Options : BookingStep.Results)
}
</script>
